import React, { useState } from 'react';
import { Plus, CreditCard, Calendar, Percent, CheckCircle2, MapPin, Crosshair, Search, Loader2 } from 'lucide-react';
import ClinicService from '../Clinic/ClinicService';

const ClinicForm = ({ formData, handleFormChange, handleFileChange, handleSubmit, onCancel, editMode, billingData = {}, handleBillingChange, subscriptionPanel }) => {
  const [uploading, setUploading] = useState({
    vendor_profile_path: false,
    licencePath: false,
    authorizedSign: false,
    commercialLicence: false,
  });

  const [geoStatus, setGeoStatus] = useState({ loading: false, message: '' });

  // 1. Detect current browser/device GPS coordinates
  const handleDetectGPS = () => {
    if (!navigator.geolocation) {
      alert('Geolocation is not supported by your browser');
      return;
    }
    setGeoStatus({ loading: true, message: 'Detecting GPS coordinates...' });
    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const lat = position.coords.latitude.toFixed(6);
        const lon = position.coords.longitude.toFixed(6);
        const latLongStr = `${lat}, ${lon}`;
        handleFormChange('vendor_latlong', latLongStr);

        // Try to reverse geocode to fill location if empty
        try {
          const res = await fetch(
            `https://nominatim.openstreetmap.org/reverse?format=jsonv2&lat=${lat}&lon=${lon}&accept-language=en`,
            { headers: { 'User-Agent': 'OrangeHealthAdmin/1.0', Accept: 'application/json' } }
          );
          const data = await res.json();
          if (data && data.address) {
            const loc = data.address.suburb || data.address.neighbourhood || data.address.city || data.address.town || data.address.state_district;
            if (loc && !formData.location) {
              handleFormChange('location', loc);
            }
          }
        } catch (_) {}

        setGeoStatus({ loading: false, message: `Detected: ${latLongStr}` });
        setTimeout(() => setGeoStatus({ loading: false, message: '' }), 4000);
      },
      (error) => {
        setGeoStatus({ loading: false, message: '' });
        alert(`Could not retrieve GPS location: ${error.message}`);
      },
      { timeout: 10000, enableHighAccuracy: true }
    );
  };

  // 2. Geocode from Location name or Clinic Address
  const handleGeocodeFromAddress = async () => {
    const query = formData.address || formData.location;
    if (!query || !query.trim()) {
      alert('Please enter a Clinic Location or Address first to find coordinates.');
      return;
    }
    setGeoStatus({ loading: true, message: `Searching coordinates for "${query.trim()}"...` });
    try {
      const res = await fetch(
        `https://nominatim.openstreetmap.org/search?format=jsonv2&q=${encodeURIComponent(query.trim())}&limit=1&accept-language=en`,
        { headers: { 'User-Agent': 'OrangeHealthAdmin/1.0', Accept: 'application/json' } }
      );
      const data = await res.json();
      if (Array.isArray(data) && data.length > 0) {
        const lat = parseFloat(data[0].lat).toFixed(6);
        const lon = parseFloat(data[0].lon).toFixed(6);
        const latLongStr = `${lat}, ${lon}`;
        handleFormChange('vendor_latlong', latLongStr);
        setGeoStatus({ loading: false, message: `Found: ${latLongStr} (${data[0].display_name.split(',').slice(0, 2).join(',')})` });
        setTimeout(() => setGeoStatus({ loading: false, message: '' }), 5000);
      } else {
        setGeoStatus({ loading: false, message: '' });
        alert(`No coordinates found for "${query}". Please verify the location/address or click "Detect GPS".`);
      }
    } catch (err) {
      setGeoStatus({ loading: false, message: '' });
      alert(`Error searching coordinates: ${err.message}`);
    }
  };

  const handleFileUpload = async (event, field) => {
    const file = event.target.files[0];
    if (!file) return;
    setUploading((prev) => ({ ...prev, [field]: true }));
    try {
      const response = await ClinicService.uploadProfilePhoto(file);
      handleFileChange(field, { target: { files: [file] } });
      handleFormChange(field, response.privatePathUrl);
    } catch (error) {
      alert(`Error uploading ${field}: ${error.message}`);
    } finally {
      setUploading((prev) => ({ ...prev, [field]: false }));
    }
  };

  // Calculate tomorrow's date for the min attribute
  const tomorrow = new Date();
  tomorrow.setDate(tomorrow.getDate() + 1);
  const minDate = tomorrow.toISOString().split('T')[0];

  return (
    <div className="form-view">
      <div className="form-header">
        <button className="back-btn" onClick={onCancel}>
          ← Back
        </button>
        <h2>{editMode ? 'Edit Clinic' : 'Create New Clinic'}</h2>
      </div>
      <div className="form-content">
        <div className="form-grid">
          <div className="form-item">
            <label className="mandatory">Profile Photo</label>
            <div className="relative w-32 h-32 mx-auto">
              <div className="w-full h-full rounded-full bg-gray-200 flex items-center justify-center overflow-hidden">
                {formData.vendor_profile_path ? (
                  <img
                    src={
                      typeof formData.vendor_profile_path === 'string'
                        ? formData.vendor_profile_path
                        : URL.createObjectURL(formData.vendor_profile_path)
                    }
                    alt="Profile Preview"
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <span className="text-gray-500">No Image</span>
                )}
              </div>
              <label className="absolute bottom-0 right-0 bg-blue-500 rounded-full p-2 cursor-pointer hover:bg-blue-600 transition-colors">
                <Plus size={20} className="text-white" />
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => handleFileUpload(e, 'vendor_profile_path')}
                  className="hidden"
                  disabled={uploading.vendor_profile_path}
                  required={!editMode}
                />
              </label>
            </div>
            {uploading.vendor_profile_path && <span className="text-sm text-gray-500">Uploading...</span>}
          </div>

          <div className="form-item">
            <label className="mandatory">Clinic Name</label>
            <input
              type="text"
              value={formData.vendorName || ''}
              onChange={(e) => handleFormChange('vendorName', e.target.value)}
              placeholder="Enter clinic name"
              required
            />
          </div>
          <div className="form-item">
            <label className="mandatory">Clinic Email</label>
            <input
              type="email"
              value={formData.vendorEmail || ''}
              onChange={(e) => handleFormChange('vendorEmail', e.target.value)}
              placeholder="Enter email"
              required
            />
          </div>
          {!editMode &&
            <div className="form-item">
              <label className="mandatory">Password</label>
              <input
                type="text"
                value={formData.password || ''}
                onChange={(e) => handleFormChange('password', e.target.value)}
                placeholder="Enter password"
                required
              />
            </div>
          }
          <div className="form-item">
            <label className="mandatory">Mobile Number</label>
            <input
              type="tel"
              value={formData.vendorConNumber || ''}
              onChange={(e) => handleFormChange('vendorConNumber', e.target.value)}
              placeholder="Enter 10-digit mobile number"
              required
              maxLength={10}
              pattern="[0-9]{10}"
              title="Please enter a valid 10-digit mobile number"
            />
          </div>
          <div className="form-item">
            <label className="mandatory">Location</label>
            <input
              type="text"
              value={formData.location || ''}
              onChange={(e) => handleFormChange('location', e.target.value)}
              placeholder="Enter location"
              required
            />
          </div>
          <div className="form-item col-span-full bg-slate-50 p-4 rounded-xl border border-slate-200">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-2">
              <div>
                <label className="font-semibold text-slate-800 flex items-center gap-1.5">
                  <MapPin className="w-4 h-4 text-blue-600" />
                  Clinic Latitude & Longitude (Coordinates)
                </label>
                <span className="text-xs text-slate-500 block">
                  Required for patient app location-based discovery. Doctors under this clinic inherit these coordinates.
                </span>
              </div>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={handleDetectGPS}
                  disabled={geoStatus.loading}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-lg bg-blue-600 text-white hover:bg-blue-700 transition shadow-xs disabled:opacity-50 cursor-pointer"
                  title="Detect GPS coordinates using device location"
                >
                  {geoStatus.loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Crosshair className="w-3.5 h-3.5" />}
                  Detect Current GPS
                </button>
                <button
                  type="button"
                  onClick={handleGeocodeFromAddress}
                  disabled={geoStatus.loading}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-lg bg-emerald-600 text-white hover:bg-emerald-700 transition shadow-xs disabled:opacity-50 cursor-pointer"
                  title="Auto-fetch coordinates from clinic location or address"
                >
                  {geoStatus.loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Search className="w-3.5 h-3.5" />}
                  Fetch from Address
                </button>
              </div>
            </div>
            <div className="relative">
              <input
                type="text"
                value={formData.vendor_latlong || ''}
                onChange={(e) => handleFormChange('vendor_latlong', e.target.value)}
                placeholder="e.g. 11.0168, 76.9558"
                className="w-full font-mono text-sm bg-white"
              />
            </div>
            {geoStatus.message && (
              <div className="mt-2 text-xs font-medium text-blue-700 bg-blue-50 border border-blue-200 px-2.5 py-1.5 rounded-lg flex items-center gap-1.5">
                <CheckCircle2 className="w-3.5 h-3.5 text-blue-600 shrink-0" />
                {geoStatus.message}
              </div>
            )}
          </div>
          <div className="form-item">
            <label className="mandatory">Clinic Registration Number</label>
            <input
              type="text"
              value={formData.regNumber || ''}
              onChange={(e) => handleFormChange('regNumber', e.target.value)}
              placeholder="Enter registration number"
              required
            />
          </div>
          <div className="form-item">
            <label className="mandatory">Clinic Address</label>
            <input
              type="text"
              value={formData.address || ''}
              onChange={(e) => handleFormChange('address', e.target.value)}
              placeholder="Enter address"
              required
            />
          </div>
          <div className="form-item">
            <label className="mandatory">Medical License Expiry Date</label>
            <input
              type="date"
              value={formData.LicenceExpDate || ''}
              onChange={(e) => handleFormChange('LicenceExpDate', e.target.value)}
              required
              min={minDate}
            />
          </div>
          {/* <div className="form-item">
            <label className="mandatory">Upload License</label>
            <input
              type="file"
              onChange={(e) => handleFileUpload(e, 'licencePath')}
              required={!editMode}
              disabled={uploading.licencePath}
            />
            {formData.licencePath && <span className="file-name">{formData.licencePath.name || 'License Uploaded'}</span>}
            {uploading.licencePath && <span className="text-sm text-gray-500">Uploading...</span>}
          </div>
          <div className="form-item">
            <label className="mandatory">Upload Authorized Signature</label>
            <input
              type="file"
              onChange={(e) => handleFileUpload(e, 'authorizedSign')}
              required={!editMode}
              disabled={uploading.authorizedSign}
            />
            {formData.authorizedSign && <span className="file-name">{formData.authorizedSign.name || 'Signature Uploaded'}</span>}
            {uploading.authorizedSign && <span className="text-sm text-gray-500">Uploading...</span>}
          </div>
          <div className="form-item">
            <label>Upload Commercial License</label>
            <input
              type="file"
              onChange={(e) => handleFileUpload(e, 'commercialLicence')}
              disabled={uploading.commercialLicence}
            />
            {formData.commercialLicence && <span className="file-name">{formData.commercialLicence.name || 'Commercial License Uploaded'}</span>}
            {uploading.commercialLicence && <span className="text-sm text-gray-500">Uploading...</span>}
          </div> */}
          {subscriptionPanel}
          {/* Legacy billing controls remain available only when the plan master is not supplied. */}
          {!subscriptionPanel && <>
          {/* ── Billing & Commission ───────────────────────────────── */}
          <div className="form-item" style={{ gridColumn: '1 / -1' }}>
            <div style={{ borderTop: '2px solid #e2e8f0', margin: '8px 0 16px', paddingTop: '16px' }}>
              <h3 style={{ fontSize: '14px', fontWeight: '700', color: '#0f766e', marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                <CreditCard size={18} className="text-teal-600" />
                Billing & Commission Configuration
              </h3>

              {/* Billing Model Selector - 2 Professional Options */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '12px', marginBottom: '20px' }}>
                {[
                  { value: 'subscription', icon: Calendar, label: 'Subscription Only', desc: 'Flat monthly or yearly platform fee' },
                  { value: 'commission',   icon: Percent,  label: 'Commission Only',   desc: 'Percentage or fixed rate per booking' },
                ].map(opt => {
                  const selected = (billingData.billing_model || 'subscription') === opt.value;
                  const Icon = opt.icon;
                  return (
                    <button
                      key={opt.value}
                      type="button"
                      onClick={() => handleBillingChange && handleBillingChange('billing_model', opt.value)}
                      style={{
                        padding: '14px 18px',
                        borderRadius: '12px',
                        border: selected ? '2px solid #0d9488' : '1px solid #e2e8f0',
                        background: selected ? '#f0fdfa' : '#ffffff',
                        boxShadow: selected ? '0 2px 8px rgba(13,148,136,0.1)' : 'none',
                        cursor: 'pointer',
                        textAlign: 'left',
                        transition: 'all 0.2s',
                        display: 'flex',
                        alignItems: 'flex-start',
                        gap: '12px',
                      }}
                    >
                      <div style={{
                        width: '36px',
                        height: '36px',
                        borderRadius: '10px',
                        background: selected ? '#ccfbf1' : '#f1f5f9',
                        color: selected ? '#0f766e' : '#64748b',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0
                      }}>
                        <Icon size={18} />
                      </div>
                      <div>
                        <div style={{ fontWeight: '700', fontSize: '13px', color: selected ? '#0f766e' : '#1e293b' }}>{opt.label}</div>
                        <div style={{ fontSize: '12px', color: selected ? '#0d9488' : '#64748b', marginTop: '2px' }}>{opt.desc}</div>
                      </div>
                    </button>
                  );
                })}
              </div>

              {/* Conditional fields */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '16px' }}>

                {/* Subscription fields */}
                {(billingData.billing_model || 'subscription') === 'subscription' && (
                  <>
                    <div className="form-item">
                      <label>Subscription Amount (₹)</label>
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        value={billingData.subscription_amount ?? ''}
                        onChange={e => handleBillingChange && handleBillingChange('subscription_amount', e.target.value)}
                        placeholder="e.g. 999"
                      />
                    </div>

                    <div className="form-item">
                      <label>Subscription Type</label>
                      <select
                        value={billingData.subscription_type || 'monthly'}
                        onChange={e => {
                          const newType = e.target.value;
                          handleBillingChange && handleBillingChange('subscription_type', newType);
                          if (billingData.billing_start_date) {
                            const d = new Date(billingData.billing_start_date);
                            if (!isNaN(d.getTime())) {
                              if (newType === 'yearly') d.setFullYear(d.getFullYear() + 1);
                              else d.setMonth(d.getMonth() + 1);
                              handleBillingChange && handleBillingChange('billing_end_date', d.toISOString().slice(0, 10));
                            }
                          }
                        }}
                      >
                        <option value="monthly">Monthly</option>
                        <option value="yearly">Yearly</option>
                      </select>
                    </div>

                    <div className="form-item">
                      <label>Billing Start Date</label>
                      <input
                        type="date"
                        value={billingData.billing_start_date || ''}
                        onChange={e => {
                          const startVal = e.target.value;
                          handleBillingChange && handleBillingChange('billing_start_date', startVal);
                          if (startVal) {
                            const d = new Date(startVal);
                            if (!isNaN(d.getTime())) {
                              if ((billingData.subscription_type || 'monthly') === 'yearly') d.setFullYear(d.getFullYear() + 1);
                              else d.setMonth(d.getMonth() + 1);
                              handleBillingChange && handleBillingChange('billing_end_date', d.toISOString().slice(0, 10));
                            }
                          }
                        }}
                      />
                    </div>

                    <div className="form-item">
                      <label>Billing End Date (Auto-calculated)</label>
                      <input
                        type="date"
                        value={billingData.billing_end_date || ''}
                        onChange={e => handleBillingChange && handleBillingChange('billing_end_date', e.target.value)}
                      />
                    </div>
                  </>
                )}

                {/* Commission fields */}
                {billingData.billing_model === 'commission' && (
                  <>
                    <div className="form-item">
                      <label>Commission Rate</label>
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        value={billingData.commission_rate ?? ''}
                        onChange={e => handleBillingChange && handleBillingChange('commission_rate', e.target.value)}
                        placeholder="e.g. 10"
                      />
                    </div>

                    <div className="form-item">
                      <label>Commission Type</label>
                      <select
                        value={billingData.commission_type || 'percentage'}
                        onChange={e => handleBillingChange && handleBillingChange('commission_type', e.target.value)}
                      >
                        <option value="percentage">Percentage (% per booking)</option>
                        <option value="fixed">Fixed Amount (₹ per booking)</option>
                      </select>
                    </div>
                  </>
                )}

              </div>
            </div>
          </div>
          </>}

          <button className="submit-btn" onClick={handleSubmit}>
            {editMode ? 'Update Clinic' : 'Create Clinic'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ClinicForm;
